import React, { memo, useCallback } from 'react';
import { styled as styled2 } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { IssueAssigneeFieldBoundary } from '@atlassian/jira-issue-field-assignee/src/ui/popover/async.tsx';
import type IssueAssigneeFieldType from '@atlassian/jira-issue-field-assignee/src/ui/popover/index.tsx';
import { useFieldValue } from '@atlassian/jira-issue-field-base/src/services/field-value-service/index.tsx';
import { AssigneeKey } from '@atlassian/jira-providers-issue/src/model/issue-system-fields.tsx';
import { lazyForPaint } from '@atlassian/react-loosely-lazy';
import { BACKLOG_PACKAGE_NAME } from '../../../../../common/constants/analytics.tsx';
import {
	setInlineEditAssigneeInteractionStartMark,
	setInlineEditAssigneeInteractionStopMark,
	cancelInlineEditAssigneeInteractionMark,
} from '../../../../../services/performance/interaction-metric-inline-edit.tsx';
import { useIsCMPBacklog } from '../../../../../state/state-hooks/capabilities.tsx';
import SLAExperienceTracker from '../../../../sla-experience-tracker/index.tsx';
import type { Person } from '../../../../../model/types.tsx';
import AssigneeFieldStatic from './assignee-field-static/index.tsx';

export const IssueAssigneeField = lazyForPaint<typeof IssueAssigneeFieldType>(
	() =>
		import(
			/* webpackChunkName: "async-issue-field-assignee" */ '@atlassian/jira-issue-field-assignee/src/ui/popover'
		),
);

export type Props = {
	isUserAtLeastMember: boolean;
	issueKey: string;
	shouldRenderRichCard: boolean;
	issueId?: string;
	assignee: Person | null;
};

const AssigneeField = ({
	isUserAtLeastMember,
	shouldRenderRichCard,
	issueKey,
	issueId,
	assignee,
}: Props) => {
	const isCompanyManaged = useIsCMPBacklog();

	const [assigneeFieldValue] = useFieldValue({
		issueKey,
		fieldKey: AssigneeKey,
	});

	const value = assigneeFieldValue
		? {
				id: assigneeFieldValue.accountId,
				displayName: assigneeFieldValue.displayName,
				avatarUrl: assigneeFieldValue.avatarUrls?.['48x48'],
			}
		: null;
	const issueAssigneeFieldStatic = (
		<AssigneeFieldStatic value={fg('jsw-backlog-assignee-field-render') ? assignee : value} />
	);

	const handleChange = useCallback(() => {
		setInlineEditAssigneeInteractionStartMark();
	}, []);

	const handleUpdate = useCallback(() => {
		setInlineEditAssigneeInteractionStopMark(isCompanyManaged);
	}, [isCompanyManaged]);

	const handleFailure = useCallback(() => {
		cancelInlineEditAssigneeInteractionMark();
	}, []);

	if (isUserAtLeastMember && shouldRenderRichCard) {
		return (
			<IssueAssigneeFieldBoundary
				packageName={BACKLOG_PACKAGE_NAME}
				fallback={issueAssigneeFieldStatic}
			>
				<SLAExperienceTracker
					experienceTarget="inline assignee"
					experienceTargetId={issueKey}
					additionalAttributes={{ issueKey }}
				>
					<AssigneeFieldWrapper>
						<IssueAssigneeField
							issueKey={issueKey}
							issueId={issueId}
							shouldPreloadAssignToMe
							onChange={handleChange}
							onUpdate={handleUpdate}
							onFailure={handleFailure}
						/>
					</AssigneeFieldWrapper>
				</SLAExperienceTracker>
			</IssueAssigneeFieldBoundary>
		);
	}

	return issueAssigneeFieldStatic;
};

export default memo<Props>(AssigneeField);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const AssigneeFieldWrapper = styled2.div({
	position: 'relative',
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& > button + div': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		position: 'absolute !important',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		top: `${token('space.400')} !important`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		left: 'auto !important',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		right: `${token('space.negative.025')} !important`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		transform: 'none !important',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'svg:first-of-type': {
			cursor: 'pointer',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			'& > path': {
				transform: 'scale(1.10638, 1) translate(-1px, 0)',
			},
		},
	},
});
